import React, { useMemo, useState } from 'react';
import {
  PHARMACY_THERAPEUTIC_GROUPS,
  filterPharmacyCommonDrugs,
  getPharmacyCommonDrug,
  pharmacyDrugFormDefaults,
} from '../constants/pharmacyCommonDrugs';
import PharmacyDrugInfoPanel from './PharmacyDrugInfoPanel';
import './PharmacyDrugNamePicker.css';

/**
 * Pharmacy add-stock: pick from common drug list or type a custom name.
 */
export default function PharmacyDrugNamePicker({
  name,
  onNameChange,
  category,
  onCategoryChange,
  unit,
  onUnitChange,
  disabled = false,
}) {
  const [mode, setMode] = useState('catalog');
  const [groupId, setGroupId] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');

  const filtered = useMemo(
    () => filterPharmacyCommonDrugs(groupId, search),
    [groupId, search]
  );

  const selectedDrug = useMemo(
    () => (selectedId ? getPharmacyCommonDrug(selectedId) : null),
    [selectedId]
  );

  const handleModeChange = (next) => {
    setMode(next);
    if (next === 'custom') {
      setSelectedId('');
    }
  };

  const handleDrugSelect = (id) => {
    setSelectedId(id);
    const drug = getPharmacyCommonDrug(id);
    if (!drug) return;
    const defaults = pharmacyDrugFormDefaults(drug);
    onNameChange(defaults.name);
    onCategoryChange(defaults.category);
    onUnitChange(defaults.unit);
  };

  return (
    <div className="pharm-drug-picker">
      <div className="pharm-drug-picker-modes" role="tablist" aria-label="How to enter medicine name">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'catalog'}
          className={`pharm-drug-picker-mode${mode === 'catalog' ? ' pharm-drug-picker-mode--active' : ''}`}
          onClick={() => handleModeChange('catalog')}
          disabled={disabled}
        >
          Pick from list
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'custom'}
          className={`pharm-drug-picker-mode${mode === 'custom' ? ' pharm-drug-picker-mode--active' : ''}`}
          onClick={() => handleModeChange('custom')}
          disabled={disabled}
        >
          Type name manually
        </button>
      </div>

      {mode === 'catalog' ? (
        <div className="pharm-drug-picker-catalog">
          <label className="pharm-drug-picker-field">
            <span className="pharm-drug-picker-label">Group (e.g. antibiotics)</span>
            <select
              className="pharm-drug-picker-select"
              value={groupId}
              onChange={(e) => {
                setGroupId(e.target.value);
                setSelectedId('');
              }}
              disabled={disabled}
            >
              <option value="">All medicines / search</option>
              {PHARMACY_THERAPEUTIC_GROUPS.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.label}
                </option>
              ))}
            </select>
          </label>
          <label className="pharm-drug-picker-field">
            <span className="pharm-drug-picker-label">Search</span>
            <input
              className="pharm-drug-picker-input"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="e.g. fever, malaria, infection, amoxicillin…"
              disabled={disabled}
              autoComplete="off"
            />
          </label>
          <label className="pharm-drug-picker-field">
            <span className="pharm-drug-picker-label">Select medicine *</span>
            <select
              className="pharm-drug-picker-select"
              value={selectedId}
              onChange={(e) => handleDrugSelect(e.target.value)}
              disabled={disabled}
            >
              <option value="">
                {filtered.length === 0 ? 'No match — type name manually' : 'Select medicine…'}
              </option>
              {filtered.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} — {d.uses.length > 48 ? `${d.uses.slice(0, 48)}…` : d.uses}
                </option>
              ))}
            </select>
          </label>
          {selectedDrug ? (
            <PharmacyDrugInfoPanel drug={selectedDrug} category={category} unit={unit} />
          ) : null}
          <p className="pharm-drug-picker-hint">
            Common generics for Tanzania. Search by name or condition. You can edit the product name below (e.g. pack
            size × 21).
          </p>
        </div>
      ) : null}

      <label className="pharm-drug-picker-field pharm-drug-picker-field--name">
        <span className="pharm-drug-picker-label">
          {mode === 'catalog' ? 'Product name (edit if needed)' : 'Product name *'}
        </span>
        <input
          className="pharm-drug-picker-input"
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="mf. Amoxicillin 500mg × 21"
          required
          disabled={disabled}
          autoComplete="off"
        />
      </label>
    </div>
  );
}
